import { useCallback, useRef, useState } from 'react';
import { cancelOngoingSpeechOnly, isSpeechSynthesisAvailable } from '@/lib/nativeTts';

/** Débit Web Speech (1 = normal ; la plupart des navigateurs plafonnent vers ~2). */
export const ROBOT_SPEECH_RATE = 1.6;

const SPECTRUM_BANDS = 16;

function pickFrenchVoice() {
  try {
    const synth = window.speechSynthesis;
    if (!synth || typeof synth.getVoices !== 'function') return null;
    const voices = synth.getVoices();
    if (!voices.length) return null;
    return (
      voices.find((v) => v.default && v.lang?.toLowerCase().startsWith('fr')) ||
      voices.find((v) => v.localService && v.lang?.toLowerCase().startsWith('fr')) ||
      voices.find((v) => v.lang?.toLowerCase().startsWith('fr')) ||
      null
    );
  } catch {
    return null;
  }
}

function bumpRandomBands(levels, count, minExtra, maxExtra) {
  for (let b = 0; b < count; b++) {
    const i = Math.floor(Math.random() * SPECTRUM_BANDS);
    const add = minExtra + Math.random() * (maxExtra - minExtra);
    levels[i] = Math.min(1, levels[i] + add);
  }
}

export const useRobotVoice = () => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechSpectrum, setSpeechSpectrum] = useState(() => Array(SPECTRUM_BANDS).fill(0));

  const generationRef = useRef(0);
  const spectrumRafRef = useRef(null);
  const levelRef = useRef(new Float32Array(SPECTRUM_BANDS));

  const stopSpectrumLoop = useCallback(() => {
    if (spectrumRafRef.current) {
      cancelAnimationFrame(spectrumRafRef.current);
      spectrumRafRef.current = null;
    }
    levelRef.current.fill(0);
    setSpeechSpectrum(Array(SPECTRUM_BANDS).fill(0));
  }, []);

  const stop = useCallback(() => {
    generationRef.current += 1;
    try {
      window.speechSynthesis?.cancel?.();
    } catch {
      /* ignore */
    }
    stopSpectrumLoop();
    setIsSpeaking(false);
  }, [stopSpectrumLoop]);

  const speak = useCallback(
    (text, onEnd = null) => {
      generationRef.current += 1;
      const myGen = generationRef.current;

      if (!isSpeechSynthesisAvailable()) {
        stopSpectrumLoop();
        setIsSpeaking(false);
        return Promise.resolve(false);
      }

      cancelOngoingSpeechOnly(window.speechSynthesis);
      stopSpectrumLoop();
      setIsSpeaking(false);

      return new Promise((resolve) => {
        const raw = String(text ?? '').trim();
        if (!raw) {
          resolve(false);
          return;
        }
        const synth = window.speechSynthesis;
        if (
          !synth ||
          typeof synth.speak !== 'function' ||
          typeof synth.getVoices !== 'function' ||
          typeof window.SpeechSynthesisUtterance !== 'function'
        ) {
          resolve(false);
          return;
        }
        let started = false;
        let settled = false;
        let startWatchdog = null;
        let speakingProbe = null;
        let interactionRetryAttached = false;
        let utteranceCancelledForRetry = false;

        const utterance = new SpeechSynthesisUtterance(raw);
        utterance.lang = 'fr-FR';
        utterance.rate = ROBOT_SPEECH_RATE;
        utterance.pitch = 2;
        utterance.volume = 1;

        const applyVoice = () => {
          const voice = pickFrenchVoice();
          if (voice) utterance.voice = voice;
        };
        applyVoice();

        const pulseState = {
          boundarySeen: false,
          fallbackTimer: null,
          fallbackInterval: null
        };

        const clearFallback = () => {
          if (pulseState.fallbackTimer) {
            clearTimeout(pulseState.fallbackTimer);
            pulseState.fallbackTimer = null;
          }
          if (pulseState.fallbackInterval) {
            clearInterval(pulseState.fallbackInterval);
            pulseState.fallbackInterval = null;
          }
        };

        const onSpeechPulse = () => {
          if (generationRef.current !== myGen) return;
          bumpRandomBands(levelRef.current, 5 + Math.floor(Math.random() * 3), 0.22, 0.48);
        };

        utterance.onboundary = (e) => {
          if (generationRef.current !== myGen) return;
          if (e.name !== 'word' && e.name !== 'sentence') return;
          pulseState.boundarySeen = true;
          clearFallback();
          onSpeechPulse();
        };

        const markStarted = () => {
          if (generationRef.current !== myGen) return;
          if (started) return;
          started = true;
          if (startWatchdog) {
            clearTimeout(startWatchdog);
            startWatchdog = null;
          }
          if (speakingProbe) {
            clearInterval(speakingProbe);
            speakingProbe = null;
          }
          setIsSpeaking(true);
          levelRef.current.fill(0);
          pulseState.boundarySeen = false;
          clearFallback();

          const loop = () => {
            if (generationRef.current !== myGen) return;
            const lv = levelRef.current;
            for (let i = 0; i < SPECTRUM_BANDS; i++) {
              let v = lv[i] * 0.9;
              v += Math.random() * 0.055;
              lv[i] = Math.min(1, v);
            }
            setSpeechSpectrum(Array.from(lv));
            spectrumRafRef.current = requestAnimationFrame(loop);
          };
          spectrumRafRef.current = requestAnimationFrame(loop);

          pulseState.fallbackTimer = setTimeout(() => {
            pulseState.fallbackTimer = null;
            if (generationRef.current !== myGen) return;
            if (!pulseState.boundarySeen) {
              pulseState.fallbackInterval = setInterval(() => {
                if (generationRef.current !== myGen) return;
                onSpeechPulse();
              }, 170);
            }
          }, 380);
        };
        utterance.onstart = markStarted;

        const done = () => {
          if (generationRef.current !== myGen) return;
          if (startWatchdog) {
            clearTimeout(startWatchdog);
            startWatchdog = null;
          }
          if (speakingProbe) {
            clearInterval(speakingProbe);
            speakingProbe = null;
          }
          clearFallback();
          if (spectrumRafRef.current) {
            cancelAnimationFrame(spectrumRafRef.current);
            spectrumRafRef.current = null;
          }
          levelRef.current.fill(0);
          setIsSpeaking(false);
          setSpeechSpectrum(Array(SPECTRUM_BANDS).fill(0));
        };

        utterance.onend = () => {
          if (settled) return;
          settled = true;
          done();
          if (onEnd) onEnd();
          const superseded = generationRef.current !== myGen;
          resolve(!superseded);
        };

        utterance.onerror = (e) => {
          if (settled) return;
          const code = typeof e?.error === 'string' ? e.error : '';
          if (
            utteranceCancelledForRetry &&
            (code === 'canceled' || code === 'cancelled' || code === 'interrupted')
          ) {
            return;
          }
          settled = true;
          done();
          resolve(false);
        };

        let launched = false;
        const go = () => {
          if (launched || generationRef.current !== myGen || settled) return;
          launched = true;
          applyVoice();
          try {
            synth.resume();
          } catch {
            /* ignore */
          }
          synth.speak(utterance);
          speakingProbe = setInterval(() => {
            if (generationRef.current !== myGen || settled) return;
            if (!started && synth.speaking) {
              markStarted();
            }
          }, 120);

          const attachInteractionRetry = () => {
            if (interactionRetryAttached || generationRef.current !== myGen || started || settled) return;
            interactionRetryAttached = true;
            const replay = () => {
              if (generationRef.current !== myGen || started || settled) return;
              try {
                utteranceCancelledForRetry = true;
                cancelOngoingSpeechOnly(synth);
                const retry = new SpeechSynthesisUtterance(raw);
                retry.lang = utterance.lang;
                retry.rate = utterance.rate;
                retry.pitch = utterance.pitch;
                retry.volume = utterance.volume;
                const voice = pickFrenchVoice();
                if (voice) retry.voice = voice;
                retry.onstart = utterance.onstart;
                retry.onboundary = utterance.onboundary;
                retry.onend = utterance.onend;
                retry.onerror = utterance.onerror;
                synth.resume();
                synth.speak(retry);
              } catch {
                /* ignore */
              }
            };
            window.addEventListener('pointerdown', replay, { once: true });
            window.addEventListener('keydown', replay, { once: true });
          };

          startWatchdog = setTimeout(() => {
            if (generationRef.current !== myGen || started || settled) return;
            try {
              utteranceCancelledForRetry = true;
              cancelOngoingSpeechOnly(synth);
              const retry = new SpeechSynthesisUtterance(raw);
              retry.lang = utterance.lang;
              retry.rate = utterance.rate;
              retry.pitch = utterance.pitch;
              retry.volume = utterance.volume;
              const voice = pickFrenchVoice();
              if (voice) retry.voice = voice;
              retry.onstart = utterance.onstart;
              retry.onboundary = utterance.onboundary;
              retry.onend = utterance.onend;
              retry.onerror = utterance.onerror;
              synth.resume();
              synth.speak(retry);
              attachInteractionRetry();
            } catch {
              settled = true;
              done();
              resolve(false);
            }
          }, 2000);
        };

        try {
          synth.getVoices();
          synth.getVoices();
        } catch {
          /* ignore */
        }
        go();
      });
    },
    [stopSpectrumLoop]
  );

  return { speak, stop, isSpeaking, speechSpectrum };
};
